import { z } from 'zod'

// Job creation schema
export const createJobSchema = z.object({
  client_org_id: z.string().trim().uuid('Invalid client organization ID'),
  arborist_id: z.string().trim().uuid('Invalid arborist ID').nullable().optional(),
  // Owning organisation — required since 5d (jobs.org_id NOT NULL)
  org_id: z.string().trim().uuid('Invalid team ID'),
  title: z
    .string()
    .min(3, 'must be at least 3 characters')
    .max(200, 'must not exceed 200 characters'),
  site_address_line1: z
    .string()
    .min(5, 'must be at least 5 characters')
    .max(500, 'must not exceed 500 characters'),
  site_latitude: z
    .number()
    .min(-90, 'Latitude must be between -90 and 90')
    .max(90, 'Latitude must be between -90 and 90')
    .nullable()
    .optional(),
  site_longitude: z
    .number()
    .min(-180, 'Longitude must be between -180 and 180')
    .max(180, 'Longitude must be between -180 and 180')
    .nullable()
    .optional(),
  description: z.string().max(2000, 'Description must not exceed 2000 characters').optional(),
  status: z.enum(['draft', 'in_progress', 'review', 'completed', 'delivered']).default('draft'),
})

// Job update schema
export const updateJobSchema = z.object({
  title: z
    .string()
    .min(3, 'Job title must be at least 3 characters')
    .max(200, 'Job title must not exceed 200 characters')
    .optional(),
  site_address_line1: z
    .string()
    .min(5, 'Site address must be at least 5 characters')
    .max(500, 'Site address must not exceed 500 characters')
    .optional(),
  site_latitude: z
    .number()
    .min(-90, 'Latitude must be between -90 and 90')
    .max(90, 'Latitude must be between -90 and 90')
    .nullable()
    .optional(),
  site_longitude: z
    .number()
    .min(-180, 'Longitude must be between -180 and 180')
    .max(180, 'Longitude must be between -180 and 180')
    .nullable()
    .optional(),
  description: z.string().max(2000, 'Description must not exceed 2000 characters').optional(),
  // z.object strips unknown keys, so anything updatable must be listed here.
  // client_org_id: reassignment from the edit dialog. org_id: interim direct
  // team change (slice 4's move function is unbuilt; RLS WITH CHECK
  // is_org_member gates the target team) — present only when changed.
  client_org_id: z.string().trim().uuid('Invalid client organization ID').optional(),
  org_id: z.string().trim().uuid('Invalid team ID').optional(),
  status: z.enum(['draft', 'in_progress', 'review', 'completed', 'delivered']).optional(),
  completed_date: z.string().optional(),
  // Printed as "Inspection date" on the report cover and Job Information.
  // A Postgres `date`; null clears it.
  scheduled_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Inspection date must be YYYY-MM-DD')
    .nullable()
    .optional(),
  // Site boundary polygon — array of vertices (min 3) or null to remove
  site_boundary: z
    .array(
      z.object({
        latitude: z.number().min(-90).max(90),
        longitude: z.number().min(-180).max(180),
      })
    )
    .min(3, 'A site boundary needs at least 3 points')
    .max(200, 'Site boundary has too many points')
    .nullable()
    .optional(),
})

// Inspection node creation schema
export const createNodeSchema = z.object({
  job_id: z.string().uuid('Invalid job ID'),
  gps_latitude: z
    .number()
    .min(-90, 'Latitude must be between -90 and 90')
    .max(90, 'Latitude must be between -90 and 90'),
  gps_longitude: z
    .number()
    .min(-180, 'Longitude must be between -180 and 180')
    .max(180, 'Longitude must be between -180 and 180'),
  gps_accuracy: z.number().min(0, 'GPS accuracy must be positive'),
  node_tag: z
    .string()
    .min(1, 'Tree tag must be at least 1 character')
    .max(100, 'Tree tag must not exceed 100 characters')
    .optional(),
  common_name: z
    .string()
    .min(2, 'Common name must be at least 2 characters')
    .max(200, 'Common name must not exceed 200 characters')
    .optional(),
  botanical_name: z
    .string()
    .min(2, 'Botanical name must be at least 2 characters')
    .max(200, 'Botanical name must not exceed 200 characters')
    .optional(),
  dbh_cm: z
    .number()
    .min(0, 'DBH must be positive')
    .max(500, 'DBH seems unrealistic (max 500 cm)')
    .optional(),
  drf_cm: z
    .number()
    .min(0, 'DRF must be positive')
    .max(500, 'DRF seems unrealistic (max 500 cm)')
    .optional(),
  height_m: z
    .number()
    .min(0, 'Height must be positive')
    .max(150, 'Height seems unrealistic (max 150 m)')
    .optional(),
  crown_spread_m: z
    .number()
    .min(0, 'Crown spread must be positive')
    .max(100, 'Crown spread seems unrealistic (max 100 m)')
    .optional(),
  condition_rating: z.enum(['excellent', 'good', 'fair', 'poor', 'critical', 'dead']).optional(),
  risk_rating: z.enum(['low', 'moderate', 'high', 'extreme']).optional(),
  invasive_status: z
    .enum(['native', 'non_native', 'invasive', 'weed_of_national_significance'])
    .optional(),
  health_notes: z.string().max(2000, 'Health notes must not exceed 2000 characters').optional(),
  risk_assessment: z
    .string()
    .max(2000, 'Risk assessment must not exceed 2000 characters')
    .optional(),
  recommended_action: z
    .string()
    .max(2000, 'Recommended action must not exceed 2000 characters')
    .optional(),
  notes: z.string().max(2000, 'Notes must not exceed 2000 characters').optional(),
})

// Inspection node update schema
export const updateNodeSchema = z.object({
  // Nullable so an existing tag can be cleared (views fall back to
  // "Node {node_number}"); undefined leaves it unchanged
  node_tag: z
    .string()
    .min(1, 'Tree tag must be at least 1 character')
    .max(100, 'Tree tag must not exceed 100 characters')
    .nullable()
    .optional(),
  common_name: z
    .string()
    .min(2, 'Common name must be at least 2 characters')
    .max(200, 'Common name must not exceed 200 characters')
    .optional(),
  botanical_name: z
    .string()
    .min(2, 'Botanical name must be at least 2 characters')
    .max(200, 'Botanical name must not exceed 200 characters')
    .optional(),
  dbh_cm: z
    .number()
    .min(0, 'DBH must be positive')
    .max(500, 'DBH seems unrealistic (max 500 cm)')
    .optional(),
  drf_cm: z
    .number()
    .min(0, 'DRF must be positive')
    .max(500, 'DRF seems unrealistic (max 500 cm)')
    .optional(),
  height_m: z
    .number()
    .min(0, 'Height must be positive')
    .max(150, 'Height seems unrealistic (max 150 m)')
    .optional(),
  crown_spread_m: z
    .number()
    .min(0, 'Crown spread must be positive')
    .max(100, 'Crown spread seems unrealistic (max 100 m)')
    .optional(),
  condition_rating: z.enum(['excellent', 'good', 'fair', 'poor', 'critical', 'dead']).optional(),
  risk_rating: z.enum(['low', 'moderate', 'high', 'extreme']).optional(),
  invasive_status: z
    .enum(['native', 'non_native', 'invasive', 'weed_of_national_significance'])
    .optional(),
  health_notes: z.string().max(2000, 'Health notes must not exceed 2000 characters').optional(),
  risk_assessment: z
    .string()
    .max(2000, 'Risk assessment must not exceed 2000 characters')
    .optional(),
  recommended_action: z
    .string()
    .max(2000, 'Recommended action must not exceed 2000 characters')
    .optional(),
  notes: z.string().max(2000, 'Notes must not exceed 2000 characters').optional(),
  gps_latitude: z
    .number()
    .min(-90, 'Latitude must be between -90 and 90')
    .max(90, 'Latitude must be between -90 and 90')
    .optional(),
  gps_longitude: z
    .number()
    .min(-180, 'Longitude must be between -180 and 180')
    .max(180, 'Longitude must be between -180 and 180')
    .optional(),
  gps_accuracy: z.number().min(0, 'GPS accuracy must be positive').optional(),
})

// Photo caption schema
export const photoCaptionSchema = z.object({
  caption: z.string().max(500, 'Caption must not exceed 500 characters').optional(),
  tags: z.array(z.string().max(50)).max(10, 'Maximum 10 tags allowed').optional(),
})

// GPS coordinates validation schema
export const gpsCoordinatesSchema = z.object({
  latitude: z
    .number()
    .min(-90, 'Latitude must be between -90 and 90')
    .max(90, 'Latitude must be between -90 and 90'),
  longitude: z
    .number()
    .min(-180, 'Longitude must be between -180 and 180')
    .max(180, 'Longitude must be between -180 and 180'),
  accuracy: z.number().min(0, 'Accuracy must be positive'),
})

// Export inferred types
export type CreateJobInput = z.infer<typeof createJobSchema>
export type UpdateJobInput = z.infer<typeof updateJobSchema>
export type CreateNodeInput = z.infer<typeof createNodeSchema>
export type UpdateNodeInput = z.infer<typeof updateNodeSchema>
export type PhotoCaptionInput = z.infer<typeof photoCaptionSchema>
export type GPSCoordinatesInput = z.infer<typeof gpsCoordinatesSchema>
